import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { SelectData } from "../../../../../models";
import { searchWorkflowTasks, SearchWorkflowTasksResponse } from "../../../../../service/query";
import { Status } from "../../../../../service/Shared";
import { KanbanColumnId, KanbanTask, Toast } from "../../../../../widget";
import { KANBAN_COLUMN_ORDER } from "../../../../../widget/general/TasksDashboard/constants";
import { TasksDashboardLane, TasksDashboardLanes, TasksDashboardQueryFilters } from "../models";
import { buildLaneRequest, buildTaskFilter, isColumnHidden, mapColumnIdToStatus, mapNodeToKanbanTask } from "../utils";
import { TASKS_DASHBOARD_PAGE_SIZE } from "./constants";

export interface UseTasksDashboardKanbanArgs {
  /** The one shared task-level filter object (BR-02828). Must be referentially stable. */
  filters: TasksDashboardQueryFilters;
  pageSize?: number;
  refreshKey?: number;
}

export interface UseTasksDashboardKanbanReturnData {
  /** The loaded cards across every lane, in lane order. */
  tasks: KanbanTask[];
  /** Per-lane connections: server `totalCount` for the column headers, plus "show more" paging. */
  lanes: TasksDashboardLanes;
  /** Tag filter options — the complete list for the filtered set, from the connection's aspects. */
  tagOptions: SelectData;
  isLoading: boolean;
  /** Loads the next page of one lane. No-op when that lane has no further pages. */
  fetchMoreLane: (columnId: KanbanColumnId) => void;
}

const createLane = (columnId: KanbanColumnId): TasksDashboardLane => ({
  columnId,
  status: mapColumnIdToStatus(columnId),
  tasks: [],
  totalCount: 0,
  hasNextPage: false,
  endCursor: null,
  isLoadingMore: false,
});

const createLanes = (): TasksDashboardLanes =>
  KANBAN_COLUMN_ORDER.reduce(
    (lanes, columnId) => ({ ...lanes, [columnId]: createLane(columnId) }),
    {} as TasksDashboardLanes
  );

const applyPage = (
  lane: TasksDashboardLane,
  response: SearchWorkflowTasksResponse,
  append: boolean
): TasksDashboardLane => {
  const tasks = response.results.map(mapNodeToKanbanTask);

  return {
    ...lane,
    tasks: append ? [...lane.tasks, ...tasks] : tasks,
    totalCount: response.paging.total,
    hasNextPage: response.paging.hasNextPage,
    endCursor: response.paging.endCursor || null,
    isLoadingMore: false,
  };
};

/**
 * The Kanban view's data: one `searchWorkflowTasks` per lane, all built from the same shared filter
 * with only `status` differing (BR-02828), and none of them restricted by `workflowObjectUuid` —
 * that omission is what makes the feed organisation-wide rather than per-project (REQ-002). Column
 * counts are the server's `totalCount`, never the number of loaded cards (REQ-004).
 *
 * It also carries the connection's `aspects.tags`, which is the tag filter's option list for every
 * view (BR-02825), so it loads regardless of the view on screen.
 */
export const useTasksDashboardKanban = ({
  filters,
  pageSize = TASKS_DASHBOARD_PAGE_SIZE,
  refreshKey = 0,
}: UseTasksDashboardKanbanArgs): UseTasksDashboardKanbanReturnData => {
  const [lanes, setLanes] = useState<TasksDashboardLanes>(createLanes);
  const [tagOptions, setTagOptions] = useState<SelectData>([]);
  const [isLoading, setIsLoading] = useState(true);

  // The shared task-level filter, built once and reused by every lane query.
  const sharedFilter = useMemo(() => buildTaskFilter(filters), [filters]);

  // Monotonic id of the latest full load, so a superseded response can't overwrite the lanes and a
  // stale cursor can't be appended to a lane the load has since replaced.
  const loadIdRef = useRef(0);

  const searchLane = useCallback(
    async (columnId: KanbanColumnId, afterCursor: string | null): Promise<SearchWorkflowTasksResponse | undefined> => {
      try {
        const response = await searchWorkflowTasks(buildLaneRequest(columnId, sharedFilter, pageSize, afterCursor));

        return response.status === Status.Success ? (response.data ?? undefined) : undefined;
      } catch {
        return undefined;
      }
    },
    [sharedFilter, pageSize]
  );

  /*
    A lane the "Hide completed" / "Hide unassigned" toggles have hidden is not queried at all — the
    board isn't rendering it, so fetching it would be a request whose result nothing can show.
  */
  const visibleColumns = useMemo(
    () => KANBAN_COLUMN_ORDER.filter((columnId) => !isColumnHidden(columnId, filters)),
    [filters]
  );

  /**
   * Loads every visible lane's first page.
   *
   * `quiet` leaves the board on the cards it already has while the new ones are fetched, rather than
   * replacing them with skeletons. That is what a reload after a mutation wants: the cards on screen
   * are still the right ones — a drag has moved one of them and is waiting to be told whether that
   * stuck — so emptying all six columns would throw away a correct board to redraw the same thing.
   * A first load has nothing to keep, and a filter change is holding cards that are now the wrong
   * set, so both of those show the skeletons.
   */
  const loadAllLanes = useCallback(
    async ({ quiet = false }: { quiet?: boolean } = {}): Promise<void> => {
      loadIdRef.current += 1;
      const loadId = loadIdRef.current;

      if (!quiet) setIsLoading(true);

      const responses = await Promise.all(visibleColumns.map((columnId) => searchLane(columnId, null)));

      // A newer load has started; its result is the one that must win.
      if (loadId !== loadIdRef.current) return;

      /*
        A lane that didn't answer keeps the cards it already had rather than being replaced by an empty
        one. Blanking it would read as "this column has no tasks", which is a statement about the data
        the request never made — during an outage that turns the whole board into an empty one nobody
        can tell from a genuinely empty organisation. The failure is said out loud instead.
      */
      const failedCount = responses.filter((response) => !response).length;
      if (failedCount > 0) {
        Toast.error({ message: "Couldn't load some of the tasks - please try again later." });
      }

      // Keyed over every column, so a hidden lane reads as an empty one rather than going missing.
      setLanes((current) =>
        KANBAN_COLUMN_ORDER.reduce((next, columnId) => {
          const index = visibleColumns.indexOf(columnId);
          if (index === -1) return { ...next, [columnId]: createLane(columnId) };

          const response = responses[index];

          return {
            ...next,
            [columnId]: response ? applyPage(createLane(columnId), response, false) : current[columnId],
          };
        }, {} as TasksDashboardLanes)
      );

      /*
        Tag options come from the connection's aspects, so they are the complete list for the filtered
        set rather than the tags of the cards that happen to be loaded (BR-02825). Every lane returns
        the same aspects for the same filter, so the union is taken only to stay correct if one failed.
      */
      const tags = new Map(
        responses.flatMap((response) => response?.aspects?.tags ?? []).map((tag) => [tag.tagUuid, tag.tagName])
      );
      setTagOptions(Array.from(tags, ([key, value]) => ({ key, value })));

      setIsLoading(false);
    },
    [searchLane, visibleColumns]
  );

  /*
    Which of the two kinds of load this is. `loadAllLanes` changes identity when the filter does, so a
    run triggered by that is a filter change; one triggered by `refreshKey` alone is a mutation asking
    for the board to be re-read, and re-reads quietly.
  */
  const lastRefreshKeyRef = useRef(refreshKey);

  useEffect(() => {
    const isRefresh = refreshKey !== lastRefreshKeyRef.current;
    lastRefreshKeyRef.current = refreshKey;

    loadAllLanes({ quiet: isRefresh });
  }, [loadAllLanes, refreshKey]);

  // Read inside fetchMoreLane so the cursor and the in-flight guard come from the current lanes
  // without the request having to live in a state updater (which React can invoke twice).
  const lanesRef = useRef(lanes);
  lanesRef.current = lanes;

  const fetchMoreLane = useCallback(
    (columnId: KanbanColumnId): void => {
      const lane = lanesRef.current[columnId];
      if (!lane.hasNextPage || lane.isLoadingMore) return;

      const loadId = loadIdRef.current;
      setLanes((prev) => ({ ...prev, [columnId]: { ...prev[columnId], isLoadingMore: true } }));

      searchLane(columnId, lane.endCursor).then((response) => {
        // Discard the page if a full load has happened since — its cursor belongs to the board before
        // that load, so appending it would mix two different task sets into one lane.
        if (loadId !== loadIdRef.current) return;

        setLanes((current) => ({
          ...current,
          [columnId]: response
            ? applyPage(current[columnId], response, true)
            : { ...current[columnId], isLoadingMore: false },
        }));
      });
    },
    [searchLane]
  );

  /*
    Memoised on the lanes, so this list only takes a new identity when the loaded cards actually
    change. The board re-syncs its own optimistic order from this prop whenever the reference moves —
    so rebuilding the array on every render would undo a drag the moment anything else re-rendered the
    page: the card would snap back to the lane it came from and then jump to the one it was dropped in
    as the reload landed.
  */
  const tasks = useMemo(() => KANBAN_COLUMN_ORDER.flatMap((columnId) => lanes[columnId].tasks), [lanes]);

  return { tasks, lanes, tagOptions, isLoading, fetchMoreLane };
};
